import {FC, useCallback, useEffect, useRef, useState} from 'react';
import {useDropzone} from 'react-dropzone';
//formik
import {FieldInputProps, FormikProps, useFormikContext} from 'formik';
//mui
import {Box, Typography, IconButton} from '@mui/material';
//icons
import {ReactComponent as AddPhotoIcon} from '@/assets/icons/add-photo.svg';
//constants
import {BODY2, CHOOSE_FILE, IMAGE_PREVIEW_ALT, TEXT} from '@/utils/constants';
//helpers
import {getImagePreview} from '@/utils/helpers';
//components
import Button from '../../Button';
//styles
import {uploadAvatarStyle} from './UploadAvatar.styles';
import {ReactComponent as CheckedErrorIcon} from '@icons/close_big.svg';

interface UploadAvatarProps {
  form?: FormikProps<any>;
  field?: FieldInputProps<string | number>;
  onChange?: (acceptedFiles: File | null) => void;
  parentClassName?: string;
}

const CHANGE_PHOTO = 'Change photo';
const SIZE = 200;
const RADIUS = '50%';
const REMOVE_ANGLE = 40;

const rad = (REMOVE_ANGLE * Math.PI) / 180;
const REMOVE_LEFT = `${50 - 50 * Math.sin(rad)}%`;
const REMOVE_BOTTOM = `${50 - 50 * Math.cos(rad)}%`;

const shapeStyle = {
  width: SIZE,
  height: SIZE,
  aspectRatio: '1 / 1',
  borderRadius: RADIUS,
  overflow: 'visible',
  boxSizing: 'border-box',
} as const;

const UploadAvatar: FC<UploadAvatarProps> = ({field, onChange, parentClassName}) => {
  const {setFieldValue} = useFormikContext<any>();

  const initialPreview = typeof field?.value === 'string' && field.value ? field.value : undefined;
  const [uploadedAvatar, setUploadedAvatar] = useState<string | undefined>(initialPreview);
  const previewRef = useRef<string | undefined>(initialPreview);

  const revokePreview = () => {
    if (previewRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(previewRef.current);
    }
  };

  // прибираємо blob при розмонтуванні
  useEffect(() => revokePreview, []);

  // синхронізація з Formik: дані з бекенду прийшли пізніше / форму скинули
  useEffect(() => {
    if (typeof field?.value === 'string' && field.value) {
      previewRef.current = field.value;
      setUploadedAvatar(field.value);
    } else if (field && !onChange && !field.value) {
      revokePreview();
      previewRef.current = undefined;
      setUploadedAvatar(undefined);
    }
  }, [field?.value]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (onChange) {
        onChange(file);
      } else if (field) {
        setFieldValue(field.name, file);
      }

      revokePreview();
      const preview = getImagePreview(file);
      previewRef.current = preview;
      setUploadedAvatar(preview);
    },
    [onChange, field, setFieldValue]
  );

  const onRemove = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (onChange) {
      onChange(null);
    } else if (field) {
      setFieldValue(field.name, null);
    }

    revokePreview();
    previewRef.current = undefined;
    setUploadedAvatar(undefined);
  };

  const {isDragAccept, getRootProps, open, getInputProps} = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: {'image/*': ['.jpeg', '.png', '.jpg']},
  });

  return (
    <Box
      {...getRootProps()}
      className={parentClassName}
      sx={{
        ...uploadAvatarStyle.wrapper,
        ...(isDragAccept && uploadAvatarStyle.dragged),
        ...shapeStyle,
        position: 'relative',
      }}
    >
      <input {...getInputProps()} />

      {uploadedAvatar ? (
        <>
          {/* фото + оверлей: обрізаються по формі */}
          <Box
            onClick={open}
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              borderRadius: 'inherit',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover .change-photo': {opacity: 1},
              '& img': {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                borderRadius: 'inherit',
              },
            }}
          >
            <img src={uploadedAvatar} alt={IMAGE_PREVIEW_ALT} draggable={false} />

            <Box
              className="change-photo"
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                opacity: 0,
                transition: 'opacity 0.2s',
                pointerEvents: 'none',
                '@media (hover: none)': {opacity: 1},
              }}
            >
              <Button
                variant={TEXT}
                disableRipple
                sx={{color: '#fff', pointerEvents: 'auto'}}
                onClick={(e) => {
                  e.stopPropagation();
                  open();
                }}
              >
                {CHANGE_PHOTO}
              </Button>
            </Box>
          </Box>

          {/* кнопка видалення: поза обрізаним контейнером, центр на краю кола */}
          <IconButton
            aria-label="Remove photo"
            size="small"
            onClick={onRemove}
            sx={{
              position: 'absolute',
              left: REMOVE_LEFT,
              bottom: REMOVE_BOTTOM,
              transform: 'translate(-50%, 50%)',
              zIndex: 2,
              color: '#fff',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              border: '2px solid #fff',
              '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.7)'},
            }}
          >
            <CheckedErrorIcon />
          </IconButton>
        </>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box sx={uploadAvatarStyle.icon}>
            <AddPhotoIcon />
          </Box>

          <Typography sx={uploadAvatarStyle.title} variant={BODY2}>
            Drag & Drop
            <br />
            or
            <Button sx={uploadAvatarStyle.button} onClick={open} disableRipple variant={TEXT} title={CHOOSE_FILE} />
            to upload
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UploadAvatar;
